import {
  type ChartsGenerics,
  formatDateTimeCharts,
  formatHourMinuteSeconds2Digits,
  formatNumericDate,
  getRange,
  getTimeInterval
} from "@quortex/front-common"
import { useSearch } from "@tanstack/react-location"
import { merge, type Options } from "highcharts"
import { useMemo } from "react"
import { chartOptions, getStepFromDuration } from "@/helpers/charts"
import type { ChartSerie } from "@/types/chart"
import type { MetricGenerics } from "@/types/metric"

const useTimeAxisParams = ({
  startTime,
  endTime,
  series
}: {
  startTime: number
  endTime: number
  series: ChartSerie[]
}) => {
  const timetamps = useMemo(
    () =>
      Array.from(
        new Set(series.flatMap(series => series.data.map(([timestamp]) => timestamp)))
      ).sort((a, b) => a - b),
    [series]
  )
  const minTime = timetamps.length ? timetamps[0] : startTime
  const maxTime = timetamps.length ? timetamps[timetamps.length - 1] : endTime
  const padding = minTime && maxTime ? (maxTime - minTime) * 0.1 : 0

  return { maxTime, minTime, padding, timetamps }
}

export type UseChartOptionsParams = {
  series: ChartSerie[]
  options?: Options
  unit?: string
  isGroupingLastSeries?: boolean
}

const usePercentageChartOptions = ({ series, options }: UseChartOptionsParams) => {
  const search = useSearch<ChartsGenerics>()
  const range = getRange(search)
  const [startTime, endTime] = getTimeInterval(range)
  const { minTime, maxTime, padding, timetamps } = useTimeAxisParams({ endTime, series, startTime })

  return useMemo(
    () =>
      merge(
        chartOptions,
        {
          chart: {
            scrollablePlotArea: {
              minWidth: 530,
              scrollPositionX: 1
            },
            type: "column"
          },
          colors: [
            "var(--color-teal-600)",
            "var(--color-purple-700)",
            "var(--color-pink-500)",
            "var(--color-orange-700)",
            "var(--color-teal-800)"
          ],
          plotOptions: {
            column: {
              borderWidth: 0,
              dataLabels: {
                color: "white",
                enabled: true,
                filter: {
                  operator: "!=",
                  property: "y",
                  value: 0
                },
                shadow: {
                  color: "black",
                  offsetX: 0,
                  offsetY: 1,
                  opacity: 0.1,
                  width: 3
                },
                style: {
                  fontSize: "0.625rem",
                  fontWeight: 700,
                  lineHeight: "1.5",
                  textOutline: "none"
                }
              },
              format: "{point.percentage:.0f}%",
              groupPadding: 0,
              pointPadding: 0,
              stacking: "percent",
              states: {
                hover: {
                  enabled: false
                },
                inactive: {
                  enabled: false
                }
              }
            },
            series: {
              centerOnCategory: true,
              groupPadding: 0,
              pointInterval: 0,
              pointPadding: 0,
              pointPlacement: "on",
              pointWidth: 28
            }
          },
          series,
          title: {
            text: ""
          },
          tooltip: {
            enabled: false
          },
          xAxis: {
            accessibility: {
              description: "Time intervals for CDN activity"
            },
            dateTimeLabelFormats: {
              day: "%e %b",
              hour: "%H:%M",
              millisecond: "%H:%M",
              minute: "%H:%M",
              month: "%b %Y",
              second: "%H:%M",
              year: "%Y"
            },
            max: maxTime ? maxTime + padding : 0,
            maxPadding: 0,
            min: minTime ? minTime - padding : 0,
            minPadding: 0,
            tickInterval: Math.floor((endTime - startTime) / 6000),
            tickPositions: timetamps,
            type: "datetime"
          },
          yAxis: {
            endOnTick: false,
            gridLineWidth: 1,
            labels: {
              format: "{value}%"
            },
            max: 100,
            min: 0,
            minorGridLineWidth: 1,
            minorTickInterval: 6.6,
            minorTicks: true,
            startOnTick: true,
            tickPositions: [0, 33, 66, 100],
            title: {
              text: ""
            }
          }
        },
        options
      ) as Options,
    [series, options, startTime, endTime, maxTime, minTime, padding, timetamps]
  )
}

const useValueChartOptions = ({
  series,
  unit,
  isGroupingLastSeries,
  options
}: UseChartOptionsParams) => {
  const search = useSearch<ChartsGenerics>()
  const range = getRange(search)
  const [startTime, endTime] = getTimeInterval(range)
  const { minTime, maxTime, padding, timetamps } = useTimeAxisParams({ endTime, series, startTime })
  const duration = endTime - startTime
  const tickInterval = getStepFromDuration(duration)

  return useMemo(
    () =>
      merge(
        chartOptions,
        {
          chart: {
            scrollablePlotArea: {
              minWidth: 530,
              scrollPositionX: 1
            },
            type: "column"
          },
          colors: [
            "var(--color-teal-600)",
            "var(--color-purple-700)",
            "var(--color-pink-500)",
            ...(isGroupingLastSeries
              ? ["var(--color-grey-500)"]
              : ["var(--color-orange-700)", "var(--color-teal-800)"])
          ],
          plotOptions: {
            column: {
              borderWidth: 0,
              groupPadding: 0.25 + 0.025 * (5 - series.length),
              pointPadding: 0,
              stacking: "",
              states: {
                hover: {
                  brightness: 0.15
                },
                inactive: {
                  enabled: false
                }
              }
            },
            series: {
              pointPadding: 0,
              pointPlacement: "on",
              pointWidth: 8
            }
          },
          series,
          title: {
            text: ""
          },
          tooltip: {
            animation: true,
            enabled: true,
            formatter: function () {
              const isAtLeastOneDay = duration >= 24 * 60 * 60 * 1000
              const previousPointDate =
                this.series.points[this.point.index - 1]?.x ?? this.series.xAxis.min
              const date =
                isAtLeastOneDay && previousPointDate
                  ? `From ${formatNumericDate(previousPointDate)}<br />
              ${formatHourMinuteSeconds2Digits(previousPointDate)} to<br />
              ${formatNumericDate(this.point.x)} ${formatHourMinuteSeconds2Digits(this.point.x)}`
                  : formatDateTimeCharts(this.point.x)

              return `
          <div class="flex flex-col">
            ${series.length > 1 ? `<span class="text-xs font-medium mb-xs">${this.series.name}</span>` : ""}
            <span class="text-xs mb-2xs">${date}</span>
            <span class="text-sm font-medium">${this.y} ${unit}</span>
          <div>`
            } as Highcharts.TooltipFormatterCallbackFunction,
            outside: true,
            useHTML: true
          },
          xAxis: {
            accessibility: {
              description: "Time intervals for CDN activity"
            },
            dateTimeLabelFormats:
              tickInterval >= 24 * 60 * 60 * 1000
                ? {
                    day: "%e %b",
                    month: "%b %Y",
                    year: "%Y"
                  }
                : {
                    hour: "%H:%M",
                    millisecond: "%H:%M",
                    minute: "%H:%M",
                    second: "%H:%M"
                  },
            max: maxTime ? maxTime + padding : 0,
            maxPadding: 0,
            min: minTime ? minTime - padding : 0,
            minPadding: 0,
            tickInterval,
            tickPositions: duration >= 2 * 24 * 60 * 60 * 1000 ? undefined : timetamps,
            type: "datetime"
          },
          yAxis: {
            gridLineWidth: 1,
            min: 0,
            minorGridLineWidth: 1,
            minorTickPerMajor: 4,
            minorTicks: true,
            title: {
              text: ""
            }
          }
        },
        options
      ) as Options,
    [
      series,
      options,
      unit,
      isGroupingLastSeries,
      duration,
      maxTime,
      minTime,
      padding,
      timetamps,
      tickInterval
    ]
  )
}

export const useMetricsChartProps = (params: Omit<UseChartOptionsParams, "unit">) => {
  const search = useSearch<MetricGenerics>()
  const percentageChartOptions = usePercentageChartOptions(params)
  const valueChartOptions = useValueChartOptions({
    ...params,
    unit: search.metric === "usage" ? "Gbps" : "viewers"
  })

  return useMemo(
    () => ({
      key: search.metric === "percentage" ? "percentage" : "value",
      options: search.metric === "percentage" ? percentageChartOptions : valueChartOptions,
      series: params.series,
      title:
        search.metric === "percentage"
          ? "Balancing"
          : search.metric === "usage"
            ? "Estimated usage"
            : "Viewers"
    }),
    [search.metric, params, percentageChartOptions, valueChartOptions]
  )
}
